import { QueryClient } from "@tanstack/react-query";

/**
 * Comprehensive logout function that clears all cached data
 * @param queryClient - React Query client instance
 * @param navigate - Navigation function to redirect after logout
 */
export const performLogout = (queryClient: QueryClient, navigate: (path: string) => void) => {
  try {
    console.log("🔄 Starting comprehensive logout...");
    
    // Clear React Query cache
    queryClient.clear();
    console.log("✅ React Query cache cleared");
    
    // Clear localStorage
    localStorage.clear();
    console.log("✅ localStorage cleared");
    
    // Clear sessionStorage if any
    sessionStorage.clear();
    console.log("✅ sessionStorage cleared");
    
    // Clear cookies
    const cookiesCleared = document.cookie.split(";").length;
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    console.log(`✅ ${cookiesCleared} cookies cleared`);
    
    // Clear any other browser storage
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
        console.log(`✅ ${names.length} caches cleared`);
      });
    }
    
    // Clear any indexedDB if used
    if ('indexedDB' in window) {
      indexedDB.databases().then(databases => {
        databases.forEach(db => {
          if (db.name) {
            indexedDB.deleteDatabase(db.name);
          }
        });
        console.log(`✅ ${databases.length} indexedDB databases cleared`);
      });
    }
    
    // Clear any service worker registrations
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
        console.log(`✅ ${registrations.length} service workers unregistered`);
      });
    }
    
    // Force garbage collection if available (Chrome only)
    if (window.gc) {
      window.gc();
      console.log("✅ Garbage collection triggered");
    }
    
    console.log("🎉 All cached data cleared successfully!");
    
    // Navigate to login page
    navigate("/login");
  } catch (error) {
    console.error("Error during logout:", error);
    // Fallback: at least clear the main storage and redirect
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  }
};

/**
 * Clear only the data cache without logging out the user
 * @param queryClient - React Query client instance
 */
export const clearDataCache = (queryClient: QueryClient) => {
  try {
    queryClient.clear();
    console.log("✅ Data cache cleared");
  } catch (error) {
    console.error("❌ Error clearing data cache:", error);
  }
};

/**
 * Clear only authentication-related data
 */
export const clearAuthData = () => {
  try {
    // Remove only auth-related items from localStorage
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_type");
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear auth-related cookies
    document.cookie.split(";").forEach((c) => {
      if (c.includes("token") || c.includes("auth") || c.includes("session")) {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      }
    });
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
};
